import { GRID_SIZE } from '../constants/gameConstants';

// Initialize a new game with two random tiles
export function initializeGame() {
  // Create empty board
  let board = Array(GRID_SIZE).fill().map(() =>
    Array(GRID_SIZE).fill(null)
  );

  // Add two random tiles to start
  board = addRandomTile(board);
  board = addRandomTile(board);

  return board;
}

// Add a random tile (2 or 4) to an empty cell
export function addRandomTile(board) {
  const emptyCells = [];

  // Find all empty cells
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (!board[i][j]) {
        emptyCells.push({ row: i, col: j });
      }
    }
  }

  // If no empty cells, return the board unchanged
  if (emptyCells.length === 0) return board;

  // Choose a random empty cell
  const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];

  // Create a copy of the board
  const newBoard = JSON.parse(JSON.stringify(board));

  // Add a new tile (90% chance of 2, 10% chance of 4)
  const value = Math.random() < 0.9 ? 2 : 4;
  newBoard[randomCell.row][randomCell.col] = {
    value,
    id: `${randomCell.row}-${randomCell.col}-${Date.now()}`,
    isNew: true,
  };

  return newBoard;
}

// Move tiles up
export function moveUp(board) {
  let moved = false;
  let score = 0;
  const boardCopy = JSON.parse(JSON.stringify(board));

  // Clear all "merged" and "isNew" flags
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (boardCopy[row][col]) {
        delete boardCopy[row][col].merged;
        delete boardCopy[row][col].isNew;
      }
    }
  }

  // Process each column
  for (let col = 0; col < GRID_SIZE; col++) {
    for (let row = 1; row < GRID_SIZE; row++) {
      if (boardCopy[row][col]) {
        let currentRow = row;

        // Move the tile up as far as possible
        while (currentRow > 0) {
          const currentTile = boardCopy[currentRow][col];
          const targetTile = boardCopy[currentRow - 1][col];

          if (!targetTile) {
            // Move to empty cell
            boardCopy[currentRow - 1][col] = currentTile;
            boardCopy[currentRow][col] = null;
            moved = true;
            currentRow--;
          } else if (targetTile.value === currentTile.value && !targetTile.merged) {
            // Merge with same-value tile
            const mergedValue = targetTile.value * 2;
            boardCopy[currentRow - 1][col] = {
              value: mergedValue,
              id: targetTile.id,
              merged: true,
              mergedFrom: [targetTile.id, currentTile.id]
            };
            boardCopy[currentRow][col] = null;
            moved = true;
            score += mergedValue;
            break;
          } else {
            // Cannot move further
            break;
          }
        }
      }
    }
  }

  return { board: boardCopy, score, moved };
}

// Move tiles down
export function moveDown(board) {
  let moved = false;
  let score = 0;
  const boardCopy = JSON.parse(JSON.stringify(board));

  // Clear all "merged" and "isNew" flags
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (boardCopy[row][col]) {
        delete boardCopy[row][col].merged;
        delete boardCopy[row][col].isNew;
      }
    }
  }

  // Process each column
  for (let col = 0; col < GRID_SIZE; col++) {
    for (let row = GRID_SIZE - 2; row >= 0; row--) {
      if (boardCopy[row][col]) {
        let currentRow = row;

        // Move the tile down as far as possible
        while (currentRow < GRID_SIZE - 1) {
          const currentTile = boardCopy[currentRow][col];
          const targetTile = boardCopy[currentRow + 1][col];

          if (!targetTile) {
            // Move to empty cell
            boardCopy[currentRow + 1][col] = currentTile;
            boardCopy[currentRow][col] = null;
            moved = true;
            currentRow++;
          } else if (targetTile.value === currentTile.value && !targetTile.merged) {
            // Merge with same-value tile
            const mergedValue = targetTile.value * 2;
            boardCopy[currentRow + 1][col] = {
              value: mergedValue,
              id: targetTile.id,
              merged: true,
              mergedFrom: [targetTile.id, currentTile.id]
            };
            boardCopy[currentRow][col] = null;
            moved = true;
            score += mergedValue;
            break;
          } else {
            // Cannot move further
            break;
          }
        }
      }
    }
  }

  return { board: boardCopy, score, moved };
}

// Move tiles left
export function moveLeft(board) {
  let moved = false;
  let score = 0;
  const boardCopy = JSON.parse(JSON.stringify(board));

  // Clear all "merged" and "isNew" flags
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (boardCopy[row][col]) {
        delete boardCopy[row][col].merged;
        delete boardCopy[row][col].isNew;
      }
    }
  }

  // Process each row
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 1; col < GRID_SIZE; col++) {
      if (boardCopy[row][col]) {
        let currentCol = col;

        // Move the tile left as far as possible
        while (currentCol > 0) {
          const currentTile = boardCopy[row][currentCol];
          const targetTile = boardCopy[row][currentCol - 1];

          if (!targetTile) {
            // Move to empty cell
            boardCopy[row][currentCol - 1] = currentTile;
            boardCopy[row][currentCol] = null;
            moved = true;
            currentCol--;
          } else if (targetTile.value === currentTile.value && !targetTile.merged) {
            // Merge with same-value tile
            const mergedValue = targetTile.value * 2;
            boardCopy[row][currentCol - 1] = {
              value: mergedValue,
              id: targetTile.id,
              merged: true,
              mergedFrom: [targetTile.id, currentTile.id]
            };
            boardCopy[row][currentCol] = null;
            moved = true;
            score += mergedValue;
            break;
          } else {
            // Cannot move further
            break;
          }
        }
      }
    }
  }

  return { board: boardCopy, score, moved };
}

// Move tiles right
export function moveRight(board) {
  let moved = false;
  let score = 0;
  const boardCopy = JSON.parse(JSON.stringify(board));

  // Clear all "merged" and "isNew" flags
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (boardCopy[row][col]) {
        delete boardCopy[row][col].merged;
        delete boardCopy[row][col].isNew;
      }
    }
  }

  // Process each row
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = GRID_SIZE - 2; col >= 0; col--) {
      if (boardCopy[row][col]) {
        let currentCol = col;

        // Move the tile right as far as possible
        while (currentCol < GRID_SIZE - 1) {
          const currentTile = boardCopy[row][currentCol];
          const targetTile = boardCopy[row][currentCol + 1];

          if (!targetTile) {
            // Move to empty cell
            boardCopy[row][currentCol + 1] = currentTile;
            boardCopy[row][currentCol] = null;
            moved = true;
            currentCol++;
          } else if (targetTile.value === currentTile.value && !targetTile.merged) {
            // Merge with same-value tile
            const mergedValue = targetTile.value * 2;
            boardCopy[row][currentCol + 1] = {
              value: mergedValue,
              id: targetTile.id,
              merged: true,
              mergedFrom: [targetTile.id, currentTile.id]
            };
            boardCopy[row][currentCol] = null;
            moved = true;
            score += mergedValue;
            break;
          } else {
            // Cannot move further
            break;
          }
        }
      }
    }
  }

  return { board: boardCopy, score, moved };
}

// Check if the game is over (no more moves possible)
export function checkGameOver(board) {
  // Check for empty cells
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!board[row][col]) {
        return false;
      }
    }
  }

  // Check for possible merges
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const currentValue = board[row][col].value;

      // Check right
      if (col < GRID_SIZE - 1 && board[row][col + 1].value === currentValue) {
        return false;
      }

      // Check below
      if (row < GRID_SIZE - 1 && board[row + 1][col].value === currentValue) {
        return false;
      }
    }
  }

  return true;
}